import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import PlaylistIcon from "@mui/icons-material/QueueMusicOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TvIcon from "@mui/icons-material/LiveTvOutlined";
import LiveIcon from "@mui/icons-material/SensorsOutlined";
import { tokens } from "../theme";
import { signageSaveErrorText } from "../utils/signageRefusal";
import ChannelMark from "./ChannelMark";
import {
  foyerApi,
  signageScopeKey,
  type SignageChannelOption,
  type SignageScopeRef,
} from "../http/foyer";
import { formatDuration } from "@shared/signage";
import {
  MIN_HOUR_WIDTH,
  hourLabelStep,
  useElementWidth,
} from "../utils/stripScale";
import type { SignageDayPlan, SignagePlaylist } from "@shared/types/signage";

/**
 * The playlist builder - compose a named 24-hour timeline from playlists and
 * channels.
 *
 * NAMING, because it bites: the design calls the thing being built a
 * "spellista" (playlist) AND calls its ingredients "spellistor" too. In the
 * model those are two different records. What this dialog saves is a
 * {@link SignageDayPlan} - a named list of `HH:mm` blocks - and what it drops
 * into that timeline are {@link SignagePlaylist} rows. The UI keeps the
 * designer's words; the code keeps the precise ones.
 *
 * The first two source tabs are a LAYER filter over one collection, not two
 * entities: global-layer playlists are the curated "Channels", company/premise
 * ones are "Playlists". That is the whole reason the model has a single
 * playlist entity.
 *
 * The third, "Live TV", is genuinely different (`BE-41`): it lists linear
 * channels from the catalogue and drops one straight onto the day. No playlist
 * is created - not even silently - because the block itself names the channel
 * and the resolver synthesises the one-item loop at build time. See
 * `SignageDayPlanBlock`.
 */

const HOURS = 24;
const DAY_MINUTES = HOURS * 60;
/** Edges snap to this. Fine enough to be useful, coarse enough to hit. */
const SNAP_MINUTES = 15;
const MIN_BLOCK_MINUTES = 15;
/** How long a block is when you click to add one. */
const DEFAULT_BLOCK_MINUTES = 120;

/*
 * The day FITS the dialog. It used to be drawn at a fixed 68px per hour, so a
 * 24-hour timeline was always 1632px wide inside a `lg` dialog that offered
 * about 1130 - the strip stopped at roughly 16:00 and the last eight hours of
 * the schedule lived off the right edge behind a scrollbar nobody looked for.
 * An operator could save a plan believing the evening was empty.
 *
 * So the geometry is proportional: every position below is a percentage of the
 * day, the track spans whatever width it is given, and `MIN_HOUR_WIDTH` is the
 * floor at which scrolling is genuinely better than compressing further. The
 * ruler stays readable by thinning its LABELS rather than by widening the day -
 * the same trick `SignageTimeline` and `SignageCampaignLanes` already use.
 */

/** Vertical geometry, compacted to match the shorter hour columns. */
const RULER_HEIGHT = 18;
const BLOCK_TOP = 6;
const BLOCK_HEIGHT = 72;
const TRACK_HEIGHT = RULER_HEIGHT + BLOCK_TOP + BLOCK_HEIGHT + 8;

/**
 * Track width to assume before the first measurement.
 *
 * Roughly what an `xl` dialog gives us on a laptop. It only decides how much of
 * a block's label is drawn for one frame - and in jsdom, where nothing has a
 * layout, it is what keeps the blocks labelled at all.
 */
const FALLBACK_TRACK_WIDTH = 1280;

/** A minute's position across the day, as a percentage of the track. */
const pctOfDay = (min: number) => (min / DAY_MINUTES) * 100;

/**
 * The three things that can go on the day.
 *
 * `channels` and `playlists` are one collection split by layer; `live` is the
 * catalogue's linear channels, which are not playlists at all (`BE-41`).
 */
type Source = "channels" | "playlists" | "live";

/**
 * Every string that changes with the tab, in one table.
 *
 * Written out rather than assembled from ternaries: the previous two-tab
 * version already had the same conditional spelled four different ways in four
 * places, and a third tab would have made that five.
 */
const SOURCE_LABELS: Record<
  Source,
  { heading: string; search: string; empty: string; noun: string }
> = {
  channels: {
    heading: "Available channels",
    search: "Search channels…",
    empty: "No channels available yet.",
    noun: "channels",
  },
  playlists: {
    heading: "Available playlists",
    search: "Search playlists…",
    empty:
      "No playlists here yet. Build one on the Playlists tab (a slide goes in via Add slide), then schedule it here.",
    noun: "playlists",
  },
  live: {
    heading: "Live TV channels",
    search: "Search live TV…",
    empty:
      "No live channels are available to schedule. They come from the content catalogue, not from your media library.",
    noun: "live channels",
  },
};

export interface BuilderBlock {
  /** Local id - the block only becomes a stored thing on save. */
  key: string;
  /** A stored loop. Mutually exclusive with `contentAssetId`. */
  playlistId?: string;
  /** A live TV channel, played with no playlist in between (`BE-41`). */
  contentAssetId?: string;
  name: string;
  owner: string;
  startMin: number;
  endMin: number;
}

/** True when this block plays a live channel rather than a stored loop. */
export const isLiveBlock = (b: BuilderBlock): boolean =>
  Boolean(b.contentAssetId);

const pad = (n: number) => n.toString().padStart(2, "0");
export const toHHMM = (min: number) =>
  `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
const label = (min: number) => `${pad(Math.floor(min / 60))}.${pad(min % 60)}`;

/**
 * `HH:mm` back to minutes-from-midnight.
 *
 * `24:00` is how the model spells end-of-day and must survive the round trip -
 * mapping it to 0 would collapse a full-day block to zero length.
 */
export function fromHHMM(hhmm: string): number {
  const [h = "0", m = "0"] = hhmm.split(":");
  return Number(h) * 60 + Number(m);
}
const snap = (min: number) => Math.round(min / SNAP_MINUTES) * SNAP_MINUTES;

/**
 * The first gap that can hold `want` minutes, else the largest gap that can
 * hold the minimum.
 *
 * Clicking a card has to put the block SOMEWHERE sensible without asking. The
 * portal's equivalent refuses outright once the day is full; here a full day
 * simply returns null and the card is disabled, which is visible before you
 * click rather than after.
 */
export function findGap(
  blocks: BuilderBlock[],
  want = DEFAULT_BLOCK_MINUTES
): { startMin: number; endMin: number } | null {
  const sorted = [...blocks].sort((a, b) => a.startMin - b.startMin);
  const gaps: { start: number; end: number }[] = [];
  let cursor = 0;
  for (const b of sorted) {
    if (b.startMin > cursor) gaps.push({ start: cursor, end: b.startMin });
    cursor = Math.max(cursor, b.endMin);
  }
  if (cursor < DAY_MINUTES) gaps.push({ start: cursor, end: DAY_MINUTES });

  const roomy = gaps.find((g) => g.end - g.start >= want);
  if (roomy) return { startMin: roomy.start, endMin: roomy.start + want };

  const biggest = gaps.sort((a, b) => b.end - b.start - (a.end - a.start))[0];
  if (biggest && biggest.end - biggest.start >= MIN_BLOCK_MINUTES) {
    return { startMin: biggest.start, endMin: biggest.end };
  }
  return null;
}

/* -------------------------------------------------------------------------- */

function TimelineBlock({
  block,
  neighbours,
  trackWidth,
  onResize,
  onRemove,
}: {
  block: BuilderBlock;
  neighbours: { minStart: number; maxEnd: number };
  /** Measured width of the whole day, in px - what a dragged pixel is worth. */
  trackWidth: number;
  onResize: (key: string, startMin: number, endMin: number) => void;
  onRemove: (key: string) => void;
}) {
  const drag = useRef<{
    edge: "start" | "end";
    originX: number;
    startMin: number;
    endMin: number;
  } | null>(null);

  const onPointerDown = (edge: "start" | "end") => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = {
      edge,
      originX: e.clientX,
      startMin: block.startMin,
      endMin: block.endMin,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // A pixel is worth whatever the day is currently drawn at, not the fixed
    // 68px-per-hour this used to assume - otherwise dragging an edge on a
    // narrow window moves it further than the pointer went.
    const deltaMin = ((e.clientX - d.originX) / trackWidth) * DAY_MINUTES;
    if (d.edge === "start") {
      // Clamped against the previous block, so dragging can never create the
      // overlap the server would reject.
      const next = Math.min(
        d.endMin - MIN_BLOCK_MINUTES,
        Math.max(neighbours.minStart, snap(d.startMin + deltaMin))
      );
      onResize(block.key, next, d.endMin);
    } else {
      const next = Math.max(
        d.startMin + MIN_BLOCK_MINUTES,
        Math.min(neighbours.maxEnd, snap(d.endMin + deltaMin))
      );
      onResize(block.key, d.startMin, next);
    }
  };

  const endDrag = () => {
    drag.current = null;
  };

  const handle = (edge: "start" | "end") => (
    <Box
      onPointerDown={onPointerDown(edge)}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      role="separator"
      aria-label={`Drag to change the ${edge} of ${block.name}`}
      sx={{
        position: "absolute",
        top: 5,
        bottom: 5,
        [edge === "start" ? "left" : "right"]: 2,
        width: 6,
        borderRadius: "3px",
        bgcolor: tokens.brand,
        opacity: 0.75,
        cursor: "ew-resize",
        touchAction: "none",
        "&:hover": { opacity: 1 },
      }}
    />
  );

  /*
   * What the block can afford to say, from its ACTUAL drawn width.
   *
   * A day that fits gives roughly 55px an hour, so a two-hour block is ~110px -
   * room for a name and its times, not for the 44px icon tile the block used to
   * carry unconditionally. Rather than clip all of it, the block drops detail as
   * it narrows and keeps the tooltip, which always has the whole story.
   */
  const durationMin = block.endMin - block.startMin;
  const widthPx = (durationMin / DAY_MINUTES) * trackWidth;
  const showIcon = widthPx >= 168;
  const showTimes = widthPx >= 104;
  const showName = widthPx >= 52;

  return (
    <Tooltip
      title={`${block.name} · ${label(block.startMin)}–${label(block.endMin)}${
        block.owner ? ` · ${block.owner}` : ""
      }`}
    >
      <Box
        sx={{
          position: "absolute",
          left: `${pctOfDay(block.startMin)}%`,
          width: `${pctOfDay(durationMin)}%`,
          // A quarter-hour block is ~11px once the day fits, which is neither
          // readable nor grabbable. The floor keeps its close button and its
          // handles reachable; it costs a few px of overlap with the next
          // block, which is the better of the two failures.
          minWidth: 40,
          top: BLOCK_TOP,
          height: BLOCK_HEIGHT,
          borderRadius: "10px",
          bgcolor: tokens.brandTintSoft,
          border: `1px solid ${tokens.brand}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: showIcon ? 1.5 : 1.25,
          // Just enough for the close button, which is 28px of a width that is
          // now measured in tens rather than hundreds.
          pr: 3.75,
          overflow: "hidden",
        }}
      >
        {handle("start")}
        {showIcon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: "8px",
              bgcolor: tokens.surface,
              display: "grid",
              placeItems: "center",
              color: tokens.brand,
            }}
          >
            {isLiveBlock(block) ? (
              <TvIcon fontSize="small" />
            ) : (
              <PlaylistIcon fontSize="small" />
            )}
          </Box>
        )}
        {showName && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink }}
            >
              {block.name}
            </Typography>
            {showTimes && (
              <Typography noWrap sx={{ fontSize: 11, color: tokens.inkMuted }}>
                {label(block.startMin)} – {label(block.endMin)}
                {block.owner ? ` | ${block.owner}` : ""}
              </Typography>
            )}
          </Box>
        )}
        <IconButton
          size="small"
          onClick={() => onRemove(block.key)}
          aria-label={`Remove ${block.name}`}
          sx={{
            position: "absolute",
            right: 5,
            top: "50%",
            mt: "-12px",
            width: 24,
            height: 24,
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {handle("end")}
      </Box>
    </Tooltip>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Lay existing playlists across a day, producing a day plan.
 *
 * NOT where a playlist is made. This consumes playlists; `SignagePlaylistDialog`
 * produces them. It was called "Create playlist" for a while and that one word
 * cost an operator a real half hour: they opened it to build their first loop
 * and were asked to pick from a list of loops that could not exist yet.
 */
export interface SignageScheduleBuilderProps {
  open: boolean;
  onClose: () => void;
  /** Where a saved plan lands: one location, or the whole customer. */
  scope: SignageScopeRef;
  /** Absent = building a new one. */
  plan?: SignageDayPlan | null;
  onSaved?: (id: string) => void;
}

export default function SignageScheduleBuilder({
  open,
  onClose,
  scope,
  plan,
  onSaved,
}: SignageScheduleBuilderProps) {
  const [name, setName] = useState("");
  const [source, setSource] = useState<Source>("playlists");
  const [query, setQuery] = useState("");
  const [blocks, setBlocks] = useState<BuilderBlock[]>([]);
  const [all, setAll] = useState<SignagePlaylist[]>([]);
  const [liveChannels, setLiveChannels] = useState<SignageChannelOption[]>([]);
  /**
   * Was live TV sold for this place at all?
   *
   * Kept apart from an empty `liveChannels` on purpose: "you have not bought
   * live TV" and "you bought it and nothing is pinned yet" need different
   * sentences, and one blank grid for both reads as a broken feature.
   */
  const [liveTvEnabled, setLiveTvEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeKey = signageScopeKey(scope);

  /*
   * How wide the day is drawn right now.
   *
   * Everything on the timeline is positioned as a percentage of the day, so
   * this is needed only for the two things percentages cannot answer: what a
   * dragged pixel is worth, and whether a block has room for its label.
   */
  const trackRef = useRef<HTMLDivElement | null>(null);
  const measuredWidth = useElementWidth(trackRef);
  const trackWidth =
    measuredWidth && measuredWidth > 0 ? measuredWidth : FALLBACK_TRACK_WIDTH;
  const labelStep = hourLabelStep(measuredWidth, HOURS);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn(scope)
      .then((list) => alive && setAll(list))
      .catch(() => alive && setAll([]));
    // A catalogue read, so it is allowed to fail without taking the dialog
    // with it: an operator laying out playlists must not be blocked because
    // the Content API is having a moment. The Live TV tab simply comes up
    // empty and says so.
    foyerApi
      .listSignageChannelOptionsIn(scope)
      .then((res) => {
        if (!alive) return;
        setLiveChannels(res.channels);
        setLiveTvEnabled(res.liveTvEnabled);
      })
      .catch(() => {
        if (!alive) return;
        setLiveChannels([]);
        // An unreachable catalogue is not a licensing answer. Staying "enabled"
        // keeps the empty state saying "we could not load these" rather than
        // telling a paying customer they never bought the product.
        setLiveTvEnabled(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope.kind, scopeKey]);

  /*
   * Seed on every opening.
   *
   * Editing loads the stored blocks; creating starts blank. Either way this
   * runs on open rather than once, because a half-built timeline reappearing
   * later is a surprise - and worse, it looks like something that was saved.
   *
   * Blocks are matched back to playlists by id, so a plan whose playlist was
   * deleted keeps its slot on the timeline with a name saying so, instead of
   * silently dropping a block an operator never chose to remove.
   */
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setError(null);
    setName(plan?.name ?? "");
    setBlocks(
      (plan?.blocks ?? []).map((b, i) => {
        // A live-channel block carries its own label, so it survives a
        // catalogue that is slow or unreachable - and unlike a playlist it
        // cannot be "deleted" out from under the plan, only unplayable, which
        // the timeline warns about at build time rather than here.
        if (b.contentAssetId) {
          const live = liveChannels.find(
            (c) => c.contentAssetId === b.contentAssetId
          );
          return {
            key: `${b.contentAssetId}-${i}`,
            contentAssetId: b.contentAssetId,
            name: live?.name ?? b.channelName ?? "Live channel",
            owner: live?.providerName ?? "Live TV",
            startMin: fromHHMM(b.start),
            endMin: fromHHMM(b.end),
          };
        }
        const p = all.find((x) => x.id === b.playlistId);
        return {
          key: `${b.playlistId}-${i}`,
          playlistId: b.playlistId,
          name: p?.name ?? "Deleted playlist",
          owner: p?.scope === "global" ? "Nenda" : "",
          startMin: fromHHMM(b.start),
          endMin: fromHHMM(b.end),
        };
      })
    );
  }, [open, plan, all, liveChannels]);

  const sources = useMemo(() => {
    if (source === "live") return [];
    const q = query.trim().toLowerCase();
    return all
      .filter((p) =>
        source === "channels" ? p.scope === "global" : p.scope !== "global"
      )
      .filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [all, source, query]);

  const liveSources = useMemo(() => {
    if (source !== "live") return [];
    const q = query.trim().toLowerCase();
    return liveChannels.filter(
      (c) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.providerName ?? "").toLowerCase().includes(q)
    );
  }, [liveChannels, source, query]);

  /** How many cards the current tab is offering - one number, three tabs. */
  const shownCount = source === "live" ? liveSources.length : sources.length;

  const sorted = useMemo(
    () => [...blocks].sort((a, b) => a.startMin - b.startMin),
    [blocks]
  );

  const gap = findGap(blocks);

  const add = useCallback(
    (p: SignagePlaylist) => {
      const slot = findGap(blocks);
      if (!slot) return;
      setBlocks((prev) => [
        ...prev,
        {
          key: `${p.id}-${slot.startMin}-${prev.length}`,
          playlistId: p.id,
          name: p.name,
          owner: p.scope === "global" ? "Nenda" : "",
          ...slot,
        },
      ]);
    },
    [blocks]
  );

  /**
   * Put a live channel on the day (`BE-41`).
   *
   * Deliberately the same gesture as adding a playlist - click a card, it lands
   * in the next gap - because to an operator "show SVT1 from 18:00" and "show
   * the lobby loop from 09:00" are the same act. The only thing that differs is
   * what the block ends up naming.
   */
  const addLive = useCallback(
    (c: SignageChannelOption) => {
      const slot = findGap(blocks);
      if (!slot) return;
      setBlocks((prev) => [
        ...prev,
        {
          key: `${c.contentAssetId}-${slot.startMin}-${prev.length}`,
          contentAssetId: c.contentAssetId,
          name: c.name,
          owner: c.providerName ?? "Live TV",
          ...slot,
        },
      ]);
    },
    [blocks]
  );

  const resize = useCallback(
    (key: string, startMin: number, endMin: number) =>
      setBlocks((prev) =>
        prev.map((b) => (b.key === key ? { ...b, startMin, endMin } : b))
      ),
    []
  );

  const remove = useCallback(
    (key: string) => setBlocks((prev) => prev.filter((b) => b.key !== key)),
    []
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        blocks: sorted.map((b) => ({
          start: toHHMM(b.startMin),
          // 24:00 is how the model spells end-of-day; midnight would read as
          // a zero-length block.
          end: b.endMin >= DAY_MINUTES ? "24:00" : toHHMM(b.endMin),
          // Exactly one of the two, which the server enforces. The channel's
          // name rides along as a LABEL only - the playable URL is always
          // resolved fresh from the id at build time.
          ...(b.contentAssetId
            ? { contentAssetId: b.contentAssetId, channelName: b.name }
            : { playlistId: b.playlistId }),
        })),
      };
      const saved = plan
        ? await foyerApi.updateSignageDayPlanIn(scope, plan.id, body)
        : await foyerApi.createSignageDayPlanIn(scope, body);
      onSaved?.(saved.id);
      onClose();
    } catch (e) {
      const err = e as { code?: string };
      setError(
        err.code === "invalid_blocks"
          ? "Some blocks overlap or are the wrong way round."
          : // A live-channel block the catalogue refuses is named by the
            // channel the operator dragged in, not by its catalogue id.
            signageSaveErrorText(
              e,
              "Could not save the schedule.",
              (assetId) =>
                blocks.find((b) => b.contentAssetId === assetId)?.name
            )
      );
    } finally {
      setSaving(false);
    }
  };

  const canSave = name.trim().length > 0 && blocks.length > 0 && !saving;

  return (
    // `xl`, like the campaign dialog: this one is a day-wide timeline, and
    // every pixel it is given is an hour that gets easier to read.
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogContent sx={{ p: 4 }}>
        <Stack direction="row" alignItems="flex-start" gap={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "10px",
              bgcolor: tokens.brandTintSoft,
              display: "grid",
              placeItems: "center",
              color: tokens.brand,
            }}
          >
            <PlaylistIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: 22, fontWeight: 700, color: tokens.ink }}
            >
              Create schedule
            </Typography>
            <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
              Lay playlists — or a live TV channel — across the day. Click a
              card to add it, drag its edges to change when it plays.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={2}
          alignItems={{ md: "flex-end" }}
          sx={{ mb: 3 }}
        >
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mb: 0.5 }}>
              Schedule name
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Name this schedule…"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <TextField
            size="small"
            placeholder={SOURCE_LABELS[source].search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ width: { xs: "100%", md: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={source}
            onChange={(_, v: Source | null) => v && setSource(v)}
          >
            <ToggleButton value="channels">
              <TvIcon fontSize="small" style={{ marginRight: 6 }} />
              Channels
            </ToggleButton>
            <ToggleButton value="playlists">
              <PlaylistIcon fontSize="small" style={{ marginRight: 6 }} />
              Playlists
            </ToggleButton>
            <ToggleButton value="live">
              <LiveIcon fontSize="small" style={{ marginRight: 6 }} />
              Live TV
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Timeline. */}
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            bgcolor: tokens.canvas,
            mb: 3,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <ScheduleIcon sx={{ fontSize: 18, color: tokens.inkMuted }} />
              <Typography
                sx={{ fontSize: 15, fontWeight: 700, color: tokens.ink }}
              >
                Timeline
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                Click a card to add it. Drag the edges to change its length.
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<DeleteSweepOutlinedIcon />}
              disabled={blocks.length === 0}
              onClick={() => setBlocks([])}
            >
              Clear timeline
            </Button>
          </Stack>

          <Box sx={{ overflowX: "auto", px: 2, pb: 2 }}>
            <Box
              ref={trackRef}
              sx={{
                position: "relative",
                // The whole day, in whatever room there is. `minWidth` is the
                // point below which compressing stops helping and scrolling is
                // the honest answer - about 530px, i.e. never on a desktop.
                width: "100%",
                minWidth: HOURS * MIN_HOUR_WIDTH,
                height: TRACK_HEIGHT,
              }}
            >
              {/* Hour ruler + gridlines. Every hour keeps a gridline; the
                  LABELS thin out when there is no room for all 24. */}
              {Array.from({ length: HOURS }, (_, h) => (
                <Box key={h}>
                  {h % labelStep === 0 && (
                    <Typography
                      sx={{
                        position: "absolute",
                        left: `${pctOfDay(h * 60)}%`,
                        ml: "4px",
                        top: 0,
                        fontSize: 10,
                        fontWeight: 500,
                        color: tokens.inkFaint,
                      }}
                    >
                      {pad(h)}.00
                    </Typography>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${pctOfDay(h * 60)}%`,
                      top: RULER_HEIGHT,
                      bottom: 8,
                      width: "1px",
                      bgcolor: tokens.divider,
                      opacity: h % labelStep === 0 ? 1 : 0.5,
                    }}
                  />
                </Box>
              ))}

              <Box
                sx={{
                  position: "absolute",
                  inset: `${RULER_HEIGHT}px 0 8px 0`,
                }}
              >
                {sorted.map((b, i) => (
                  <TimelineBlock
                    key={b.key}
                    block={b}
                    neighbours={{
                      minStart: i === 0 ? 0 : sorted[i - 1]!.endMin,
                      maxEnd:
                        i === sorted.length - 1
                          ? DAY_MINUTES
                          : sorted[i + 1]!.startMin,
                    }}
                    trackWidth={trackWidth}
                    onResize={resize}
                    onRemove={remove}
                  />
                ))}

                {/* Where the next card will land - shown before you click, so
                    adding is predictable rather than a surprise. */}
                {gap && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${pctOfDay(gap.startMin)}%`,
                      width: `${pctOfDay(gap.endMin - gap.startMin)}%`,
                      minWidth: 40,
                      top: BLOCK_TOP,
                      height: BLOCK_HEIGHT,
                      borderRadius: "10px",
                      border: `1px dashed ${tokens.divider}`,
                      display: "grid",
                      placeItems: "center",
                      color: tokens.inkFaint,
                      fontSize: 12,
                      overflow: "hidden",
                      pointerEvents: "none",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <AddIcon fontSize="small" />
                      {/* The sentence needs about this much room; below it the
                          dashed outline says the same thing on its own. */}
                      {((gap.endMin - gap.startMin) / DAY_MINUTES) *
                        trackWidth >=
                        130 && (
                        <Typography noWrap sx={{ fontSize: 12 }}>
                          Next item lands here
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Source cards. */}
        <Typography
          sx={{ fontSize: 17, fontWeight: 700, color: tokens.ink, mb: 1.5 }}
        >
          {SOURCE_LABELS[source].heading}
        </Typography>

        {shownCount === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${tokens.divider}`,
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
              {query
                ? `Nothing matches “${query}”.`
                : source === "live" && !liveTvEnabled
                  ? // The commercial answer, said plainly. Live TV on a wall is
                    // Signage AND Streaming TV together; a place holding only
                    // the first has not bought this, and telling them so is a
                    // conversation a salesperson can have. A bare "no channels
                    // available" would read as a fault in the product.
                    "Live TV is not part of this location’s subscription. Channels on a signage screen need Streaming TV alongside Signage — talk to Nenda to add it."
                  : SOURCE_LABELS[source].empty}
            </Typography>
          </Box>
        ) : source === "live" ? (
          /*
           * A CAROUSEL, not the wrapping grid the other two tabs use.
           *
           * Those list things the operator authored - a handful of playlists
           * they made themselves - and a grid that grows downward is right for
           * a set you own. This lists what the LOCATION WAS SOLD: a fixed
           * line-up in the order they already dragged into place on the
           * Channels page. Wrapping it into rows destroys that order visually
           * (position 4 lands under position 1) and pushes the timeline off
           * screen the moment a hotel has thirty channels. One horizontal band
           * keeps the whole line-up in its own sequence and keeps the day plan
           * above the fold, which is what the operator is actually editing.
           */
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              // Scrollable content inside its own container - the dialog body
              // must never scroll sideways.
              scrollSnapType: "x proximity",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                background: tokens.divider,
                borderRadius: 4,
              },
            }}
          >
            {liveSources.map((c) => (
              <Box
                key={c.contentAssetId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: "12px",
                  bgcolor: tokens.surface,
                  opacity: gap ? 1 : 0.5,
                  // Fixed width and no shrinking: a flex row would otherwise
                  // squeeze every card until the names are unreadable rather
                  // than letting the strip scroll.
                  width: 280,
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                }}
              >
                {/* One mark everywhere - see `ChannelMark`. This tile drew the
                    raw `logoUrl` in a tinted box, which meant an empty square
                    for every channel whose artwork is a storage path, and an
                    invisible one for every white-on-transparent mark. */}
                <ChannelMark
                  src={c.logoUrl}
                  name={c.name}
                  id={c.contentAssetId}
                  size={52}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: 15, fontWeight: 600, color: tokens.ink }}
                  >
                    {c.name}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: 12, color: tokens.inkMuted }}
                  >
                    {c.providerName ?? "Live TV"}
                  </Typography>
                  {/* No duration, and that is the point: the block's own time
                      range is how long the channel holds the screen. */}
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    Plays for the whole block
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    gap
                      ? "Click to add"
                      : "The day is full - remove a block first"
                  }
                >
                  <span>
                    <IconButton
                      onClick={() => addLive(c)}
                      disabled={!gap}
                      aria-label={`Add ${c.name} to the timeline`}
                      sx={{
                        border: `1px solid ${tokens.divider}`,
                        borderRadius: "8px",
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            }}
          >
            {sources.map((p) => {
              const totalSeconds = p.items.reduce(
                (sum, i) =>
                  sum + (i.durationSeconds || p.defaultItemSeconds || 0),
                0
              );
              return (
                <Box
                  key={p.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "12px",
                    bgcolor: tokens.surface,
                    opacity: gap ? 1 : 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: "8px",
                      bgcolor: tokens.brandTintSoft,
                      display: "grid",
                      placeItems: "center",
                      color: tokens.brand,
                    }}
                  >
                    <PlaylistIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{ fontSize: 15, fontWeight: 600, color: tokens.ink }}
                    >
                      {p.name}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ fontSize: 12, color: tokens.inkMuted }}
                    >
                      {p.scope === "global"
                        ? "Nenda"
                        : p.scope === "company"
                          ? "Shared by the customer"
                          : "This location"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {p.items.length} {p.items.length === 1 ? "item" : "items"}{" "}
                      · {formatDuration(totalSeconds)}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      gap
                        ? "Click to add"
                        : "The day is full - remove a block first"
                    }
                  >
                    <span>
                      <IconButton
                        onClick={() => add(p)}
                        disabled={!gap}
                        aria-label={`Add ${p.name} to the timeline`}
                        sx={{
                          border: `1px solid ${tokens.divider}`,
                          borderRadius: "8px",
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3 }}
        >
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
            {error ? (
              <Box component="span" sx={{ color: tokens.error }}>
                {error}
              </Box>
            ) : (
              `Showing ${shownCount} ${SOURCE_LABELS[source].noun}`
            )}
          </Typography>
          <Stack direction="row" gap={1}>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!canSave} onClick={save}>
              {saving ? "Saving…" : "Save schedule"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
